// URL monitoring — logs 404s and legacy redirect hits to Supabase
// so admins can spot problem URLs after the migration.
//
// Client-side, best-effort: never throws, silently dedupes within a session,
// and also forwards to GA4 (dataLayer) when available.

import { supabase } from '@/integrations/supabase/client';

export type UrlEventKind = '404' | 'legacy_html' | 'legacy_redirect';

const SESSION_KEY = 'urlEvents:sent';

function alreadySent(fingerprint: string): boolean {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    const set = new Set<string>(raw ? JSON.parse(raw) : []);
    if (set.has(fingerprint)) return true;
    set.add(fingerprint);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...set].slice(-50)));
    return false;
  } catch {
    return false;
  }
}

export function logUrlEvent(kind: UrlEventKind, path: string, language?: string): void {
  if (typeof window === 'undefined' || !path) return;
  const trimmedPath = path.slice(0, 2048);
  const fingerprint = `${kind}::${trimmedPath}`;
  if (alreadySent(fingerprint)) return;

  const payload = {
    kind,
    path: trimmedPath,
    referrer: (document.referrer || null)?.slice(0, 2048) ?? null,
    user_agent: (navigator.userAgent || null)?.slice(0, 512) ?? null,
    language: language ? language.slice(0, 8) : null,
  };

  // Forward to GA4 if present
  try {
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer?.push({
      event: 'url_issue',
      url_issue_kind: kind,
      url_issue_path: trimmedPath,
    });
  } catch {
    /* ignore */
  }

  // Fire-and-forget insert; never surfaces errors to the user
  void supabase
    .from('url_events')
    .insert(payload)
    .then(({ error }) => {
      if (error) console.warn('[urlMonitor] insert failed:', error.message);
    });
}
